import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import {
  ICategory,
  ICreateCategoryPayload,
  IDeleteCategoryPayload,
  ICategoryGoodsCount,
  ICategoryGoodsCountPayload,
  ICategoryListParams,
  IUpdateCategoryPayload,
} from 'entities/Category/Category.models';
import { categoriesTransport } from 'entities/Category/Category.transport';

interface IState {
  category: ICategory | null;
  categoryList: ICategory[];
  categoryCount: number;
  isCategoryLoading: boolean;
  categoryGoodsCountItems: ICategoryGoodsCount[];
}

const initialState: IState = {
  category: null,
  categoryList: [],
  categoryCount: 0,
  isCategoryLoading: false,
  categoryGoodsCountItems: [],
};

export const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    setCategory(state, action: PayloadAction<ICategory | null>) {
      state.category = action.payload;
    },
    setCategoryList(state, action: PayloadAction<ICategory[]>) {
      state.categoryList = action.payload;
    },
    setCategoryCount(state, action: PayloadAction<number>) {
      state.categoryCount = action.payload;
    },
    setIsCategoryLoading(state, action: PayloadAction<boolean>) {
      state.isCategoryLoading = action.payload;
    },
    setCategoryGoodsCountItems(state, action: PayloadAction<ICategoryGoodsCount[]>) {
      state.categoryGoodsCountItems = action.payload;
    },
    addCategoryListItem(state, action: PayloadAction<ICategory>) {
      state.categoryList.push(action.payload);
    },
    patchCategoryListItem(state, action: PayloadAction<ICategory>) {
      const category = state.categoryList.find((item) => item.id === action.payload.id);

      if (!category) {
        return;
      }

      Object.assign(category, action.payload);
    },
    removeCategoryListItem(state, action: PayloadAction<number>) {
      state.categoryList = state.categoryList.filter((item) => item.id !== action.payload);

      if (state.category?.id === action.payload) {
        state.category = null;
      }
    },
  },
});

export const {
  setCategory,
  setCategoryList,
  setCategoryCount,
  setIsCategoryLoading,
  setCategoryGoodsCountItems,
  addCategoryListItem,
  patchCategoryListItem,
  removeCategoryListItem,
} = categorySlice.actions;

export const selectCategory = (state: RootState) => state.category.category;
export const selectCategoryList = (state: RootState) => state.category.categoryList;
export const selectCategoryCount = (state: RootState) => state.category.categoryCount;
export const selectIsCategoryLoading = (state: RootState) => state.category.isCategoryLoading;
export const selectCategoryGoodsCountItems = (state: RootState) => state.category.categoryGoodsCountItems;

export const categoryReducer = categorySlice.reducer;

export const getCategoryList =
  ({ onSuccess, ...rest }: ICategoryListParams) =>
  async (dispatch: AppDispatch) => {
    dispatch(setIsCategoryLoading(true));

    try {
      const response = await categoriesTransport.get(rest);
      dispatch(setCategoryList(response.data));
      dispatch(setCategoryCount(response.count));
      onSuccess?.();
      return response.data;
    } catch (error) {
      axiosErrorHandler(error);
      return null;
    } finally {
      dispatch(setIsCategoryLoading(false));
    }
  };

export const getCategoriesGoodsCountItems = (payload: ICategoryGoodsCountPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsCategoryLoading(true));

  try {
    const response = await categoriesTransport.getGoodsCount(payload);
    dispatch(setCategoryGoodsCountItems(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsCategoryLoading(false));
  }
};

export const getCategoryById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsCategoryLoading(true));

  try {
    const response = await categoriesTransport.getById(id);
    dispatch(setCategory(response));
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsCategoryLoading(false));
  }
};

export const createCategory = (payload: ICreateCategoryPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsCategoryLoading(true));

  try {
    const response = await categoriesTransport.create(payload);
    dispatch(addCategoryListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsCategoryLoading(false));
  }
};

export const updateCategory = (payload: IUpdateCategoryPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsCategoryLoading(true));

  try {
    const response = await categoriesTransport.update(payload);
    dispatch(setCategory(response));
    dispatch(patchCategoryListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsCategoryLoading(false));
  }
};

export const deleteCategory = (payload: IDeleteCategoryPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsCategoryLoading(true));

  try {
    const response = await categoriesTransport.delete(payload);
    dispatch(removeCategoryListItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsCategoryLoading(false));
  }
};
